import { createFileRoute, useNavigate, Link as RouterLink } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  Dialog,
  DropdownMenu,
  Flex,
  Grid,
  Heading,
  IconButton,
  Link,
  Select,
  Separator,
  Text,
  TextArea,
  TextField,
  Tooltip
} from '@radix-ui/themes'
import {
  ArchiveIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  Link2Icon,
  UploadIcon
} from '@radix-ui/react-icons'
import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import { Controller, useForm, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import cn from 'classnames'
import { Ops } from '@willie/schemas'
import { parseStoredImageRef } from '@willie/supabase/storage'
import { useAuth } from '~/auth/AuthProvider'
import { CopyButton } from '~/components/CopyButton'
import {
  ConfirmActionButton,
  ConfirmDialogContent,
  useConfirmAction,
  type ConfirmSpec
} from '~/components/ConfirmAction'
import { FieldShell } from '~/components/FieldShell'
import { ImageLightbox, openOnPlainClick } from '~/components/ImageLightbox'
import { useTRPC } from '~/utils/trpc'
import {
  IMAGE_DROP_ACCEPT,
  IMAGE_MAX_BYTES,
  STAGE_OPTIONS,
  STALLED_BECAUSE_OF_OPTIONS,
  stalledBecauseOfLabel,
  type StalledBecauseOf
} from '~/features/installations/constants'
import { toUploadableImage } from '~/features/installations/heic'
import {
  canReceiveInstructions,
  compatibilityCheckedDate,
  computeStage,
  deletePreInstallationImage,
  formSchema,
  needsInstructions,
  toFormValues,
  uploadPreInstallationImage,
  type FormValues,
  type InstallationWithRelations
} from '~/features/installations/service'
import { ContactPicker } from '~/features/contacts/components/ContactPicker'
import { OrgLink } from '~/features/organizations/components/OrgLink'
import { InstallationComments, eventDomId } from '~/features/installations/components/InstallationComments'
import { CompatibilityCard } from '~/features/installations/components/CompatibilityCard'
import { InstallationCaptures } from '~/features/installations/components/InstallationCaptures'
import { InstallationPhotos } from '~/features/installations/components/InstallationPhotos'
import { MeterPhotosCard } from '~/features/meter-photos/components/MeterPhotosCard'
import { PhotoLightboxFooter } from '~/features/installations/components/PhotoLightboxFooter'
import { ShareImageLinkDialog } from '~/features/installations/components/ShareImageLinkDialog'
import { useOpsSignedUrl } from '~/features/installations/useOpsSignedUrl'
import { InstallationTickets } from '~/features/freshdesk/components/InstallationTickets'
import { InstallStationForm } from '~/features/installations/components/InstallStationForm'
import { AddDataSourceDialog } from '~/features/installations/components/AddDataSourceDialog'
import { RetireInstallationDialog } from '~/features/installations/components/RetireInstallationDialog'
import { CreateMaterialReturnDialog } from '~/features/material-returns/components/CreateMaterialReturnDialog'
import { InstallationMaterialReturns } from '~/features/material-returns/components/InstallationMaterialReturns'
import { buildDeviceDescription, isSupportedDataSourceMaker } from '~/features/installations/dataSource'
import { buildProviderDeviceLink } from '~/features/installations/providerLinks'
import { integerDaysSince } from '~/features/installations/overview'
import { todayIso } from '~/utils/date'
import { webappSiteUrl, webappStationUrl } from '~/utils/webapp'

// Stage actions sit in a 120px stepper column, where a label like "Mark order placed" wraps to two
// lines. Radix pins a size-1 button to a 24px height, so the second line paints outside it; h-auto
// lets the box follow the text, and the floor keeps single-line buttons the height they already were.
const STAGE_BUTTON_CLASS = 'h-auto min-h-6 w-full whitespace-normal py-1 leading-tight'

const scrollToEvent = (eventId: number) => {
  const el = document.getElementById(eventDomId(eventId))
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.animate([{ backgroundColor: 'var(--red-a4)' }, { backgroundColor: 'transparent' }], {
    duration: 1500,
    easing: 'ease-out'
  })
}

const ViewInstallationEventLink = ({ eventId }: { eventId: number }) => (
  <Link
    size="2"
    href={`#${eventDomId(eventId)}`}
    onClick={(e) => {
      e.preventDefault()
      scrollToEvent(eventId)
    }}
  >
    View event
  </Link>
)

const TitleSeparator = () => (
  <Text color="gray" weight="regular" aria-hidden>
    {' / '}
  </Text>
)

// "Assigned: <name>" pill; renders nothing when the stall has no assignee.
const AssigneeBadge = ({ assignee }: { assignee: Ops.StationInstallations.StallAssignee }) => {
  const name = Ops.StationInstallations.assigneeDisplayName(assignee)
  if (!name) return null
  return (
    <Badge color="gray" variant="soft" size="1">
      Assigned: {name}
    </Badge>
  )
}

const reminderWarning = (dateStr: string): string => {
  const days = integerDaysSince(dateStr)
  if (days === 0) return 'A reminder was already sent today.'
  if (days === 1) return 'A reminder was already sent yesterday.'
  return `A reminder was already sent ${days} days ago.`
}

const shippedSubtitles = (
  installation: Pick<
    InstallationWithRelations,
    'logger_shipping_date' | 'instruction_sent_date' | 'installation_reminder_date' | 'site_id' | 'site_contact'
  >
): string[] => {
  if (!installation.logger_shipping_date) return []
  const instructions = installation.instruction_sent_date
    ? `Instructed ${installation.instruction_sent_date}`
    : canReceiveInstructions(installation)
      ? 'Instructions queued'
      : 'No site contact'
  const reminded = installation.installation_reminder_date
    ? [`Reminded on ${installation.installation_reminder_date}`]
    : []
  return [instructions, ...reminded]
}

const ShippingInstructionsNote = ({
  installation
}: {
  installation: Pick<InstallationWithRelations, 'instruction_sent_date' | 'site_id' | 'site_contact'>
}) => {
  if (installation.instruction_sent_date) return null
  if (!canReceiveInstructions(installation)) {
    return (
      <Text as="p" size="2" mt="2" color="orange">
        No site contact email: instructions won't go out until one is set.
      </Text>
    )
  }
  return (
    <Text as="p" size="2" mt="2">
      The next scheduled run emails the instructions to {installation.site_contact?.email}, in one email with the
      site's other shipped meters.
    </Text>
  )
}

type StageAction = {
  label: string
  isDone: boolean
  disabled?: boolean
  onSelect: () => void | Promise<void>
  confirm?: ConfirmSpec
}

interface ConfirmActionItemProps {
  label: string
  itemDisabled: boolean
  onConfirm: () => void | Promise<void>
  confirm: ConfirmSpec
}

const ConfirmActionItem = ({ label, itemDisabled, onConfirm, confirm }: ConfirmActionItemProps) => {
  const { open, handleOpenChange, handleClick } = useConfirmAction(itemDisabled, confirm, onConfirm)

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Trigger>
        <DropdownMenu.Item disabled={itemDisabled} onSelect={(e) => e.preventDefault()}>
          {label}
        </DropdownMenu.Item>
      </AlertDialog.Trigger>
      <ConfirmDialogContent confirm={confirm} onConfirm={handleClick} />
    </AlertDialog.Root>
  )
}

interface MarkShippedButtonProps {
  disabled: boolean
  instructionsNote: ReactNode
  pending: boolean
  errorMessage: string | null
  // Called on confirm with the ISO date (YYYY-MM-DD) the user picked; resolves on success.
  onConfirm: (shippingDate: string) => Promise<void>
  onOpen: () => void
}

// "Mark as shipped" opens a dialog to confirm and pick the shipping date (defaults to today).
const MarkShippedButton = ({
  disabled,
  instructionsNote,
  pending,
  errorMessage,
  onConfirm,
  onOpen
}: MarkShippedButtonProps) => {
  const [open, setOpen] = useState(false)
  const [shippingDate, setShippingDate] = useState(todayIso())

  const handleOpenChange = (next: boolean) => {
    if (next && disabled) return
    setOpen(next)
    if (next) {
      setShippingDate(todayIso())
      onOpen()
    }
  }

  const handleConfirm = async () => {
    if (!shippingDate) return
    try {
      await onConfirm(shippingDate)
      setOpen(false)
    } catch {
      // keep dialog open; errorMessage renders below
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <Button size="1" className={STAGE_BUTTON_CLASS} disabled={disabled}>
          Mark as shipped
        </Button>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>Mark as shipped</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Record when the hardware was shipped. Defaults to today.
        </Dialog.Description>
        {instructionsNote}
        <Box mt="3">
          <Text as="label" size="1" color="gray" className="block mb-1">
            Shipping date
          </Text>
          <TextField.Root
            type="date"
            value={shippingDate}
            onChange={(e) => setShippingDate(e.target.value)}
            autoFocus
          />
        </Box>
        {errorMessage ? (
          <Text color="red" size="2" as="p" mt="3">
            {errorMessage}
          </Text>
        ) : null}
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={handleConfirm} loading={pending} disabled={!shippingDate || pending}>
            Mark as shipped
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

// Radix Select.Item forbids an empty value, so "no assignee" needs a sentinel.
const UNASSIGNED = 'unassigned'

interface StallDialogItemProps {
  itemDisabled: boolean
  pending: boolean
  errorMessage: string | null
  initialNote: string
  initialBecauseOf?: StalledBecauseOf
  initialAssignedToUserId?: string | null
  onConfirm: (note: string, category: StalledBecauseOf, assignedToUserId: string | null) => Promise<void>
  onOpen: () => void
}

const { INSTALLATION_EVENT_NOTE_MAX_LENGTH } = Ops.StationInstallations

const StallDialogItem = ({
  itemDisabled,
  pending,
  errorMessage,
  initialNote,
  initialBecauseOf = 'client',
  initialAssignedToUserId = null,
  onConfirm,
  onOpen
}: StallDialogItemProps) => {
  const trpc = useTRPC()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState(initialNote)
  const [becauseOf, setBecauseOf] = useState<StalledBecauseOf>(initialBecauseOf)
  const [assignedToUserId, setAssignedToUserId] = useState<string | null>(initialAssignedToUserId)

  const employeesQuery = useQuery(
    trpc.ops.stationInstallations.listWillieEmployees.queryOptions(undefined, { enabled: open })
  )
  const employees = employeesQuery.data ?? []

  const triggerLabel = 'Edit stall note'

  const handleOpenChange = (next: boolean) => {
    if (next && itemDisabled) return
    setOpen(next)
    if (next) {
      setNote(initialNote)
      setBecauseOf(initialBecauseOf)
      setAssignedToUserId(initialAssignedToUserId)
      onOpen()
    }
  }

  const handleSubmit = async () => {
    const trimmed = note.trim()
    if (!trimmed) return
    try {
      await onConfirm(trimmed, becauseOf, assignedToUserId)
      setOpen(false)
    } catch {
      // keep dialog open; errorMessage renders below
    }
  }

  const trimmedLength = note.trim().length
  const canSubmit = trimmedLength > 0 && trimmedLength <= INSTALLATION_EVENT_NOTE_MAX_LENGTH && !pending

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <DropdownMenu.Item disabled={itemDisabled} onSelect={(e) => e.preventDefault()}>
          {triggerLabel}
        </DropdownMenu.Item>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>Edit stall note</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Keep the event note and ownership current while this installation is stalled.
        </Dialog.Description>
        <Box mt="3">
          <Text as="label" size="1" color="gray" className="block mb-1">
            Stalled because of
          </Text>
          <Select.Root value={becauseOf} onValueChange={(v) => setBecauseOf(v as StalledBecauseOf)}>
            <Select.Trigger className="w-full" />
            <Select.Content>
              {STALLED_BECAUSE_OF_OPTIONS.map((option) => (
                <Select.Item key={option} value={option}>
                  {stalledBecauseOfLabel[option]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box mt="3">
          <Text as="label" size="1" color="gray" className="block mb-1">
            Assigned to
          </Text>
          <Select.Root
            value={assignedToUserId ?? UNASSIGNED}
            onValueChange={(v) => setAssignedToUserId(v === UNASSIGNED ? null : v)}
          >
            <Select.Trigger className="w-full" placeholder="Unassigned" />
            <Select.Content>
              <Select.Item value={UNASSIGNED}>Unassigned</Select.Item>
              {employees.map((employee) => (
                <Select.Item key={employee.userId} value={employee.userId}>
                  {employee.fullName}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box mt="3">
          <Text as="label" size="1" color="gray" className="block mb-1">
            Note
          </Text>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Waiting on customer to confirm meter location"
            rows={4}
            maxLength={INSTALLATION_EVENT_NOTE_MAX_LENGTH}
            autoFocus
          />
          <Text size="1" color="gray" className="block mt-1">
            {trimmedLength}/{INSTALLATION_EVENT_NOTE_MAX_LENGTH}
          </Text>
        </Box>
        {errorMessage ? (
          <Text color="red" size="2" as="p" mt="3">
            {errorMessage}
          </Text>
        ) : null}
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} loading={pending} disabled={!canSubmit}>
            Save changes
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

type TextFieldKey = 'plannedInstallationDate' | 'loggerSerialNumber' | 'pulseWeight'

interface TextFieldEditorProps {
  label: string
  name: TextFieldKey
  control: Control<FormValues>
  type?: 'text' | 'email' | 'url' | 'date' | 'number'
  placeholder?: string
  // Maps the raw input to the form value; defaults to an empty string -> null text mapping.
  parse?: (raw: string) => string | number | null
  // Unit shown as a right-aligned slot inside the input (e.g. 'L').
  unit?: string
  action?: ReactNode
}

const TextFieldEditor = ({
  label,
  name,
  control,
  type = 'text',
  placeholder,
  parse = (raw) => raw || null,
  unit,
  action
}: TextFieldEditorProps) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <FieldShell
        label={label}
        action={action}
        hint={
          error ? (
            <Text size="1" color="red">
              {error.message}
            </Text>
          ) : null
        }
      >
        <TextField.Root
          type={type}
          step={type === 'number' ? 'any' : undefined}
          value={field.value ?? ''}
          placeholder={placeholder}
          onBlur={field.onBlur}
          onChange={(e) => field.onChange(parse(e.target.value))}
        >
          {unit ? (
            <TextField.Slot side="right">
              <Text size="2" className="text-gray-11">
                {unit}
              </Text>
            </TextField.Slot>
          ) : null}
        </TextField.Root>
      </FieldShell>
    )}
  />
)

interface InstallDialogProps {
  installationId: number
  // Controlled by the page: the stepper cell that opens this unmounts the moment the stage goes
  // done, which is mid-flow for a successful install, so the dialog cannot live inside it.
  open: boolean
  onOpenChange: (open: boolean) => void
  orgName: string | null
  defaultLoggerMaker: string | null
  defaultSerialNumber: string | null
  defaultPulseWeight: number | null
}

const InstallDialog = ({
  installationId,
  open,
  onOpenChange,
  orgName,
  defaultLoggerMaker,
  defaultSerialNumber,
  defaultPulseWeight
}: InstallDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  // The form defers the `get` invalidation until it finishes, so an early exit (escape, cancel)
  // must refetch it to bring the installed state back in sync.
  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content maxWidth="560px">
        <Dialog.Title>Install station</Dialog.Title>
        <InstallStationForm
          installationId={installationId}
          orgName={orgName}
          defaultLoggerMaker={defaultLoggerMaker}
          defaultSerialNumber={defaultSerialNumber}
          defaultPulseWeight={defaultPulseWeight}
          onClose={() => onOpenChange(false)}
        />
      </Dialog.Content>
    </Dialog.Root>
  )
}

interface ProgressStage {
  label: string
  date: string | null
  done?: boolean
  subtitles?: string[]
  // Control shown in the date slot while this is the current (first pending) stage.
  action?: ReactNode
}

const ProgressStepper = ({ stages }: { stages: ProgressStage[] }) => {
  const isStageDone = (stage: ProgressStage): boolean => stage.done ?? !!stage.date
  const firstPendingIndex = stages.findIndex((s) => !isStageDone(s))

  return (
    <Card>
      <Flex align="start" gap="0" className="w-full">
        {stages.map((stage, i) => {
          const isDone = isStageDone(stage)
          const isCurrent = i === firstPendingIndex
          const isLast = i === stages.length - 1
          return (
            <Fragment key={stage.label}>
              <Flex direction="column" align="center" gap="2" className="shrink-0 w-32">
                <Flex
                  align="center"
                  justify="center"
                  className={cn('w-8 h-8 rounded-full', {
                    'bg-[var(--accent-9)] text-white': isDone,
                    'border-2 border-[var(--accent-9)]': !isDone && isCurrent,
                    'border-2 border-[color:var(--gray-6)]': !isDone && !isCurrent
                  })}
                >
                  {isDone ? (
                    <CheckIcon />
                  ) : (
                    <Text
                      size="2"
                      weight="medium"
                      color={isCurrent ? undefined : 'gray'}
                      className={isCurrent ? 'text-[var(--accent-11)]' : undefined}
                    >
                      {i + 1}
                    </Text>
                  )}
                </Flex>
                <Text size="1" weight="medium" align="center" className="px-1">
                  {stage.label}
                </Text>
                <Flex direction="column" align="center" gap="1" className="w-full px-1">
                  {isDone ? (
                    <Text size="1" color="gray" align="center">
                      {stage.date ?? 'Done'}
                    </Text>
                  ) : isCurrent && stage.action ? (
                    <Box className="w-full">{stage.action}</Box>
                  ) : (
                    <Text size="1" color="gray" align="center">
                      —
                    </Text>
                  )}
                  {stage.subtitles?.map((subtitle) => (
                    <Text key={subtitle} size="1" color="gray" align="center" className="whitespace-nowrap">
                      {subtitle}
                    </Text>
                  ))}
                </Flex>
              </Flex>
              {!isLast && (
                <Box
                  className={cn('h-[2px] mt-[15px] flex-1', {
                    'bg-[var(--accent-9)]': isDone,
                    'bg-[var(--gray-6)]': !isDone
                  })}
                />
              )}
            </Fragment>
          )
        })}
      </Flex>
    </Card>
  )
}

interface PreInstallationImageProps {
  /** Stored reference: an ops storage path, a legacy public URL, or a third-party URL. */
  storedRef: string | null
  installationId: number
  canEdit: boolean
  onUploaded: (path: string) => void
  /** Clears the row's reference; awaited so a failure keeps the stored object. */
  onDeleted: () => Promise<void>
}

const PreInstallationImage = ({
  storedRef,
  installationId,
  canEdit,
  onUploaded,
  onDeleted
}: PreInstallationImageProps) => {
  const { supabaseClient } = useAuth()
  const [errored, setErrored] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const { data: signedUrl, isPending: isResolving } = useOpsSignedUrl(storedRef)

  const parsedRef = storedRef ? parseStoredImageRef(storedRef) : null
  const sharePath = parsedRef?.kind === 'opsPath' ? parsedRef.path : null

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview)
    }
  }, [localPreview])

  // Signed URLs rotate, so a load failure must not latch — otherwise one expired
  // link leaves the tile permanently showing an error after it has been re-signed.
  useEffect(() => {
    setErrored(false)
  }, [signedUrl])

  const displayUrl = localPreview ?? signedUrl ?? null
  const showImage = displayUrl && !errored
  const resolving = !!storedRef && isResolving && !localPreview
  const canDrop = canEdit && !uploading

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: IMAGE_DROP_ACCEPT,
    maxSize: IMAGE_MAX_BYTES,
    disabled: !canDrop,
    noClick: !!showImage,
    onDropRejected: (rejections) => {
      const first = rejections[0]?.errors[0]?.message ?? 'File not accepted'
      setActionError(first)
    },
    onDrop: async ([file]) => {
      if (!file) return
      setActionError(null)
      setUploading(true)
      try {
        const image = await toUploadableImage(file)
        setLocalPreview(URL.createObjectURL(image))
        const path = await uploadPreInstallationImage(supabaseClient, installationId, image)
        setErrored(false)
        onUploaded(path)
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Upload failed')
        setLocalPreview(null)
      } finally {
        setUploading(false)
      }
    }
  })

  const rootProps = canDrop ? getRootProps() : {}

  const deleteImage = async () => {
    setActionError(null)
    try {
      // Row first: a failure here leaves the image intact and retryable, whereas removing the
      // object first and then failing leaves a row pointing at nothing — and the retry lives in
      // the lightbox, which an unloadable image makes unreachable. The reverse order only ever
      // orphans an object, which the next upload overwrites at the same deterministic path.
      await onDeleted()
      // The lightbox closes itself once the image is gone. Closing it here would unmount it
      // in the same tick as the confirm dialog nested inside it.
      setLocalPreview(null)
      // Stops links already shared with a customer from resolving. Legacy rows point at someone
      // else's URL, where there is nothing of ours to remove.
      if (sharePath) await deletePreInstallationImage(supabaseClient, sharePath)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <Flex direction="column" gap="2" className="h-full">
      <Box
        {...rootProps}
        className={cn(
          'relative min-h-[12rem] w-full flex-1 overflow-hidden rounded-[var(--radius-3)] border bg-[var(--gray-2)]',
          canDrop && !showImage && 'cursor-pointer border-dashed',
          isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-5)]'
        )}
      >
        {canDrop && <input {...getInputProps()} />}
        {showImage ? (
          <>
            <a
              href={displayUrl}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0 block"
              onClick={openOnPlainClick(() => setOpenIndex(0))}
            >
              <img
                src={displayUrl}
                alt="Pre-installation"
                className="h-full w-full object-cover"
                onError={() => setErrored(true)}
              />
            </a>
            {canDrop && isDragActive ? (
              <Flex
                align="center"
                justify="center"
                gap="2"
                direction="column"
                className="absolute inset-0 bg-[var(--accent-2)]/85 pointer-events-none"
              >
                <UploadIcon width="24" height="24" color="var(--accent-9)" />
                <Text size="2" weight="medium">
                  Drop to replace
                </Text>
              </Flex>
            ) : null}
          </>
        ) : uploading ? (
          <Flex align="center" justify="center" className="h-full w-full">
            <Text size="2" color="gray">
              Uploading…
            </Text>
          </Flex>
        ) : resolving ? (
          <Flex align="center" justify="center" className="h-full w-full">
            <Text size="2" color="gray">
              Loading…
            </Text>
          </Flex>
        ) : storedRef ? (
          // Ahead of the dropzone prompt: an image on record that will not display is
          // a different situation from having none, and saying "drag to upload" here
          // reads as though the row were empty.
          <Flex direction="column" align="center" justify="center" gap="1" className="h-full w-full px-3">
            <Text size="2" color="gray" align="center">
              Image failed to load
            </Text>
            {canDrop ? (
              <Text size="1" color="gray" align="center">
                {isDragActive ? 'Drop image here' : 'Drop a new image to replace it'}
              </Text>
            ) : null}
          </Flex>
        ) : canDrop ? (
          <Flex direction="column" align="center" justify="center" gap="2" className="h-full w-full">
            <UploadIcon width="24" height="24" color="var(--gray-9)" />
            <Text size="2" color="gray" align="center" className="px-3">
              {isDragActive ? 'Drop image here' : 'Drag image or click to upload'}
            </Text>
            <Text size="1" color="gray">
              PNG / JPG / WebP · up to 10MB
            </Text>
          </Flex>
        ) : (
          <Flex align="center" justify="center" className="h-full w-full">
            <Text size="2" color="gray">
              No pre-install image
            </Text>
          </Flex>
        )}
        {/* Only while the image is not on screen — when it is, sharing lives in the
            lightbox, as it does for the gallery. */}
        {sharePath && !showImage ? (
          <ShareImageLinkDialog
            path={sharePath}
            trigger={
              <IconButton
                size="1"
                variant="solid"
                color="gray"
                title="Copy a link to share with a customer"
                className="absolute right-1 top-1"
                // The dropzone root accepts clicks whenever no image is shown.
                onClick={(event) => event.stopPropagation()}
              >
                <Link2Icon width="14" height="14" />
              </IconButton>
            }
          />
        ) : parsedRef?.kind === 'foreignUrl' ? (
          // Says why there is no share button. Without this, "hosted elsewhere" is
          // indistinguishable from the feature being broken. The title sits on the
          // wrapper because a disabled button shows no tooltip.
          <span
            className="absolute right-1 top-1"
            title="Hosted outside Willie — this image has its own permanent link, so there is nothing to re-share"
          >
            <IconButton size="1" variant="soft" color="gray" disabled>
              <ExternalLinkIcon width="14" height="14" />
            </IconButton>
          </span>
        ) : null}
      </Box>
      {actionError ? (
        <Text size="1" color="red">
          {actionError}
        </Text>
      ) : null}
      <ImageLightbox
        images={displayUrl ? [displayUrl] : []}
        openIndex={openIndex}
        onOpenChange={setOpenIndex}
        footer={
          <PhotoLightboxFooter
            sharePath={sharePath}
            onDelete={
              canEdit
                ? {
                    run: () => void deleteImage(),
                    confirmTitle: 'Delete pre-installation photo?',
                    confirmDescription:
                      'This permanently removes the photo and un-ticks the “Image received” step. This cannot be undone.'
                  }
                : undefined
            }
          />
        }
      />
    </Flex>
  )
}

const LinkDataItem = ({
  label,
  value,
  valueNode,
  subtle,
  subtleHref
}: {
  label: string
  value: string | null
  valueNode?: ReactNode
  subtle?: string | null
  subtleHref?: string | null
}) => (
  <DataList.Item>
    <DataList.Label>{label}</DataList.Label>
    <DataList.Value>
      <Flex direction="column">
        {valueNode ?? <Text size="2">{value ?? '—'}</Text>}
        {subtle ? (
          <Flex align="center" gap="2">
            {subtleHref ? (
              <Link href={subtleHref} target="_blank" rel="noreferrer" size="1" className="font-mono">
                {subtle}
              </Link>
            ) : (
              <Text size="1" color="gray" className="font-mono">
                {subtle}
              </Text>
            )}
            <CopyButton value={subtle} label={`Copy ${label.toLowerCase()} ID`} iconOnly />
          </Flex>
        ) : null}
      </Flex>
    </DataList.Value>
  </DataList.Item>
)

const InstallationDetailPage = () => {
  const { installationId: idParam } = Route.useParams()
  const installationId = Number(idParam)
  const navigate = useNavigate()
  const [installOpen, setInstallOpen] = useState(false)
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const installationQuery = useQuery(trpc.ops.stationInstallations.get.queryOptions({ installationId }))
  const installation = installationQuery.data

  const eventsQuery = useQuery(trpc.ops.stationInstallations.events.list.queryOptions({ installationId }))
  const installationEvents = eventsQuery.data ?? []
  const currentStall =
    installationEvents.find((event) => event.type === 'stalled' && event.resolvedAt === null) ?? null

  // Includes retired ones: this list is only the prev/next walk, and omitting them would leave a
  // hole exactly where the operator is standing after retiring the installation on screen.
  const installationsListQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions({ includeRetired: true })
  )
  const allIds = installationsListQuery.data?.map((i) => i.installation_id) ?? []
  const currentIndex = allIds.indexOf(installationId)
  const prevId = currentIndex > 0 ? allIds[currentIndex - 1] : null
  const nextId = currentIndex >= 0 && currentIndex < allIds.length - 1 ? allIds[currentIndex + 1] : null

  const siteGroupsQuery = useQuery(trpc.ops.stationInstallations.siteGroups.queryOptions({ installationId }))
  const instructionIds = siteGroupsQuery.data?.instructions ?? []
  const reminderIds = siteGroupsQuery.data?.reminder ?? []

  const goToInstallation = (id: number) => {
    navigate({ to: '/installations/$installationId', params: { installationId: String(id) } })
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: installation ? toFormValues(installation) : undefined,
    resetOptions: { keepDirtyValues: true }
  })

  const updateMutation = useMutation(
    trpc.ops.stationInstallations.update.mutationOptions({
      onSuccess: (updated) => {
        // Write the server response into the detail cache immediately so stage
        // completion (e.g. the shipped date) reflects without a refetch flash.
        // Spreading over prev keeps embedded relations, which `updated` omits.
        queryClient.setQueryData(trpc.ops.stationInstallations.get.queryKey({ installationId }), (prev) =>
          prev ? { ...prev, ...updated } : prev
        )
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
      }
    })
  )

  const deleteMutation = useMutation(
    trpc.ops.stationInstallations.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        navigate({ to: '/installations' })
      }
    })
  )

  const sendReminderMutation = useMutation(
    trpc.ops.stationInstallations.sendReminderForSite.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.siteGroups.queryKey() })
      }
    })
  )

  const sendInstructionsMutation = useMutation(
    trpc.ops.stationInstallations.sendInstructionsForSite.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.siteGroups.queryKey() })
      }
    })
  )

  const requestIndexCaptureMutation = useMutation(
    trpc.ops.indexCaptures.requestCapture.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.ops.stationInstallations.events.list.queryKey({ installationId })
        })
      }
    })
  )

  const invalidateEvents = () => {
    queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
    queryClient.invalidateQueries({
      queryKey: trpc.ops.stationInstallations.events.list.queryKey({ installationId })
    })
    queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.listStalled.queryKey() })
  }

  const resolveEventMutation = useMutation(
    trpc.ops.stationInstallations.events.resolve.mutationOptions({ onSuccess: invalidateEvents })
  )

  const updateEventMutation = useMutation(
    trpc.ops.stationInstallations.events.update.mutationOptions({ onSuccess: invalidateEvents })
  )

  const onSubmit = handleSubmit((data) => {
    updateMutation.mutate({ installationId, ...data }, { onSuccess: () => reset(data) })
  })

  const handleDelete = () => {
    deleteMutation.mutate({ installationId })
  }

  if (installationQuery.isLoading) {
    return <Text color="gray">Loading…</Text>
  }
  if (installationQuery.error) {
    return <Text color="red">{installationQuery.error.message}</Text>
  }
  if (!installation) {
    return <Text color="gray">Installation not found.</Text>
  }

  const isStalled = currentStall !== null
  const isRetired = !!installation.retired_at
  // Falls back to the installation's own name, which is what the station was named at creation.
  const stationName = installation.water_station?.name ?? installation.name

  // Provisioning is offered only while the station has no meter source: a second one would leave
  // two sources feeding the same station, which the install procedure also refuses.
  const canAddDataSource =
    !isRetired &&
    !!installation.water_station_id &&
    isSupportedDataSourceMaker(installation.logger_maker) &&
    (installation.water_station?.data_sources ?? []).every((source) => source.source_type !== 'meter')

  const providerDeviceLink = buildProviderDeviceLink({
    dataSources: installation.water_station?.data_sources ?? [],
    shaypAccountId: installation.org?.shayp_account_id ?? null
  })

  const isActionPending =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    sendReminderMutation.isPending ||
    sendInstructionsMutation.isPending ||
    requestIndexCaptureMutation.isPending ||
    resolveEventMutation.isPending ||
    updateEventMutation.isPending

  // Retiring closes the installation to further work; the stepper is replaced by a banner below.
  const actionsDisabled = isActionPending || isRetired

  const markShipped = (shippingDate: string): Promise<void> =>
    updateMutation.mutateAsync({ installationId, loggerShippingDate: shippingDate }).then(() => undefined)

  const stageActions: StageAction[] = [
    {
      label: 'Set as placed',
      isDone: !!installation.order_placed_date,
      onSelect: () => updateMutation.mutate({ installationId, orderPlacedDate: todayIso() })
    },
    {
      label: 'Mark instructions as sent',
      isDone: !!installation.instruction_sent_date,
      onSelect: () => updateMutation.mutate({ installationId, instructionSentDate: todayIso() })
    },
    {
      label: 'Mark as aligned',
      isDone: !!installation.index_aligned_date,
      onSelect: () => updateMutation.mutate({ installationId, indexAlignedDate: todayIso() })
    },
    {
      label: 'Send instructions now',
      isDone: !!installation.instruction_sent_date,
      disabled: !installation.site_contact?.email || instructionIds.length === 0,
      onSelect: () =>
        sendInstructionsMutation.mutateAsync({ installationIds: instructionIds }).then(() => undefined),
      confirm: {
        title: `Send instructions to ${installation.site_contact?.email ?? ''}?`,
        description: `One email covering the ${instructionIds.length} meter(s) this contact is still waiting on, marking each of them sent.`,
        actionLabel: 'Send',
        pending: sendInstructionsMutation.isPending,
        errorMessage: sendInstructionsMutation.error?.message ?? null,
        onOpen: () => sendInstructionsMutation.reset()
      }
    },
    {
      label: 'Request index capture',
      isDone: false,
      disabled: !installation.site_id || !installation.site_contact?.email,
      onSelect: () => requestIndexCaptureMutation.mutateAsync({ installationId }).then(() => undefined),
      confirm: {
        title: `Ask ${installation.site_contact?.email ?? ''} for a meter photo?`,
        description:
          'Sends the index-capture email now, outside the scheduled rounds. The daily job is unaffected, and the reading is compared against telemetry the same way.',
        actionLabel: 'Send',
        pending: requestIndexCaptureMutation.isPending,
        errorMessage: requestIndexCaptureMutation.error?.message ?? null,
        onOpen: () => requestIndexCaptureMutation.reset()
      }
    },
    {
      label: 'Send reminder',
      isDone: false,
      disabled: !installation.site_contact?.email || reminderIds.length === 0,
      onSelect: () => sendReminderMutation.mutateAsync({ installationIds: reminderIds }).then(() => undefined),
      confirm: {
        title: `Send reminder to ${installation.site_contact?.email ?? ''}?`,
        description: `One email covering the ${reminderIds.length} meter(s) this contact has yet to install. It counts against the three-reminder cap.`,
        actionLabel: 'Send',
        pending: sendReminderMutation.isPending,
        warning: installation.installation_reminder_date
          ? reminderWarning(installation.installation_reminder_date)
          : null,
        errorMessage: sendReminderMutation.error?.message ?? null,
        onOpen: () => sendReminderMutation.reset()
      }
    },
    {
      label: 'Manually reminded',
      isDone: false,
      onSelect: () => updateMutation.mutate({ installationId, installationReminderDate: todayIso() })
    }
  ]

  const resumeAction: StageAction & { confirm: ConfirmSpec } = {
    label: 'Resume installation',
    isDone: false,
    onSelect: () => resolveEventMutation.mutateAsync({ eventId: currentStall!.id }).then(() => undefined),
    confirm: {
      title: 'Resume this installation?',
      description: 'It will no longer appear in the stalled list on the home screen.',
      actionLabel: 'Resume',
      pending: resolveEventMutation.isPending,
      errorMessage: resolveEventMutation.error?.message ?? null,
      onOpen: () => resolveEventMutation.reset()
    }
  }

  const findStageAction = (label: string): StageAction | undefined =>
    stageActions.find((action) => action.label === label)

  const hintText = (text: string): ReactNode => (
    <Text size="1" color="gray" align="center" as="p">
      {text}
    </Text>
  )

  // Compact, column-width button for a stage's action, shown under its stepper node.
  const stageButton = (label: string): ReactNode => {
    const action = findStageAction(label)
    if (!action) return null
    const disabled = action.isDone || !!action.disabled || actionsDisabled
    if (action.confirm) {
      return (
        <ConfirmActionButton
          label={action.label}
          disabled={disabled}
          size="1"
          className={STAGE_BUTTON_CLASS}
          onConfirm={action.onSelect}
          confirm={action.confirm}
        />
      )
    }
    return (
      <Button size="1" className={STAGE_BUTTON_CLASS} disabled={disabled} onClick={() => void action.onSelect()}>
        {action.label}
      </Button>
    )
  }

  const sendInstructions = findStageAction('Send instructions now')
  const markInstructionSent = findStageAction('Mark instructions as sent')
  const sendReminder = findStageAction('Send reminder')
  const manuallyReminded = findStageAction('Manually reminded')
  const requestIndexCapture = findStageAction('Request index capture')
  const awaitingInstructions = needsInstructions(installation)

  // Derived from the stage rather than a date: an installation ordered before this step existed has passed it.
  const compatibilityChecked =
    STAGE_OPTIONS.indexOf(computeStage(installation)) >= STAGE_OPTIONS.indexOf('compatibility_checked')

  const stepperStages: ProgressStage[] = [
    { label: 'Install created', date: installation.created_at.slice(0, 10) },
    {
      label: 'Image received',
      date: installation.pre_installation_image_added_at,
      action: hintText('Upload the image on the right')
    },
    {
      label: 'Compatibility checked',
      date: compatibilityCheckedDate(installation),
      done: compatibilityChecked
    },
    { label: 'Order placed', date: installation.order_placed_date, action: stageButton('Set as placed') },
    {
      label: 'Hardware shipped',
      date: installation.logger_shipping_date,
      subtitles: shippedSubtitles(installation),
      action: (
        <MarkShippedButton
          disabled={!!installation.logger_shipping_date || actionsDisabled}
          instructionsNote={<ShippingInstructionsNote installation={installation} />}
          pending={updateMutation.isPending}
          errorMessage={updateMutation.error?.message ?? null}
          onConfirm={markShipped}
          onOpen={() => updateMutation.reset()}
        />
      )
    },
    {
      label: 'Station installed',
      date: installation.installation_date,
      action: (
        <Button
          size="1"
          className={STAGE_BUTTON_CLASS}
          disabled={actionsDisabled}
          onClick={() => setInstallOpen(true)}
        >
          Install station
        </Button>
      )
    },
    { label: 'Index aligned', date: installation.index_aligned_date, action: stageButton('Mark as aligned') }
  ]

  return (
    <Flex direction="column" gap="4">
      <Flex align="center" justify="between" wrap="wrap" gap="3">
        <Flex align="center" gap="3">
          <Heading size="5">
            {installation.org?.name ? (
              <>
                <OrgLink orgId={installation.org_id} name={installation.org.name} />
                <TitleSeparator />
              </>
            ) : null}
            {installation.site?.name && installation.site_id ? (
              <>
                <Link asChild>
                  <RouterLink to="/stations" search={{ orgId: installation.org_id, siteId: installation.site_id }}>
                    {installation.site.name}
                  </RouterLink>
                </Link>
                <TitleSeparator />
              </>
            ) : null}
            {stationName && installation.water_station_id ? (
              <Link asChild>
                <RouterLink to="/stations/$stationId" params={{ stationId: installation.water_station_id }}>
                  {stationName}
                </RouterLink>
              </Link>
            ) : (
              (stationName ?? 'Unnamed station')
            )}
            <TitleSeparator />
            <Text className="font-mono">#{installation.installation_id}</Text>
          </Heading>
          {isStalled && (
            <Badge color="red" variant="soft">
              stalled
            </Badge>
          )}
          {installation.retired_at ? (
            <Tooltip content={installation.retirement_reason ?? 'No reason recorded'}>
              <Badge color="gray" variant="soft">
                retired {installation.retired_at}
              </Badge>
            </Tooltip>
          ) : null}
          <Flex align="center" gap="2">
            <IconButton
              variant="soft"
              color="gray"
              size="2"
              disabled={prevId === null}
              onClick={() => prevId !== null && goToInstallation(prevId)}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              variant="soft"
              color="gray"
              size="2"
              disabled={nextId === null}
              onClick={() => nextId !== null && goToInstallation(nextId)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Flex>
        </Flex>

        <Flex gap="2" align="center">
          {/* Hosted here, triggered from the stepper: the stage cell that opens this unmounts
              as soon as the install lands, which is while its result is still on screen. */}
          <InstallDialog
            installationId={installationId}
            open={installOpen}
            onOpenChange={setInstallOpen}
            orgName={installation.org?.name ?? null}
            defaultLoggerMaker={installation.logger_maker}
            defaultSerialNumber={installation.logger_serial_number}
            defaultPulseWeight={installation.pulse_weight}
          />

          {installation.retired_at ? null : (
            <RetireInstallationDialog installationId={installationId}>
              <Button variant="soft" color="red" disabled={actionsDisabled}>
                Retire
              </Button>
            </RetireInstallationDialog>
          )}

          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button variant="soft" disabled={isActionPending}>
                Action
                <ChevronDownIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {canAddDataSource ? (
                <>
                  <AddDataSourceDialog
                    installationId={installationId}
                    defaultLoggerMaker={installation.logger_maker}
                    defaultSerialNumber={installation.logger_serial_number}
                    defaultPulseWeight={installation.pulse_weight}
                    defaultDeviceDescription={buildDeviceDescription(
                      installation.org?.name ?? null,
                      installationId
                    )}
                    trigger={
                      <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                        Add data source
                      </DropdownMenu.Item>
                    }
                  />
                  <DropdownMenu.Separator />
                </>
              ) : null}
              {awaitingInstructions && sendInstructions?.confirm ? (
                <ConfirmActionItem
                  label={sendInstructions.label}
                  itemDisabled={!!sendInstructions.disabled || actionsDisabled}
                  onConfirm={sendInstructions.onSelect}
                  confirm={sendInstructions.confirm}
                />
              ) : null}
              {awaitingInstructions && markInstructionSent ? (
                <DropdownMenu.Item disabled={actionsDisabled} onSelect={() => void markInstructionSent.onSelect()}>
                  {markInstructionSent.label}
                </DropdownMenu.Item>
              ) : null}
              {sendReminder?.confirm ? (
                <ConfirmActionItem
                  label={sendReminder.label}
                  itemDisabled={actionsDisabled}
                  onConfirm={sendReminder.onSelect}
                  confirm={sendReminder.confirm}
                />
              ) : null}
              {requestIndexCapture?.confirm ? (
                <ConfirmActionItem
                  label={requestIndexCapture.label}
                  itemDisabled={!!requestIndexCapture.disabled}
                  onConfirm={requestIndexCapture.onSelect}
                  confirm={requestIndexCapture.confirm}
                />
              ) : null}
              {manuallyReminded ? (
                <DropdownMenu.Item
                  disabled={manuallyReminded.isDone || actionsDisabled}
                  onSelect={() => void manuallyReminded.onSelect()}
                >
                  {manuallyReminded.label}
                </DropdownMenu.Item>
              ) : null}
              <CreateMaterialReturnDialog
                orgId={installation.org_id}
                installationId={installationId}
                defaultContactId={installation.site_contact_id}
                trigger={
                  <DropdownMenu.Item disabled={isActionPending} onSelect={(event) => event.preventDefault()}>
                    Request material return
                  </DropdownMenu.Item>
                }
              />
              {currentStall ? (
                <>
                  <DropdownMenu.Separator />
                  <StallDialogItem
                    itemDisabled={actionsDisabled}
                    pending={updateEventMutation.isPending}
                    errorMessage={updateEventMutation.error?.message ?? null}
                    initialNote={currentStall.note}
                    initialBecauseOf={currentStall.category ?? 'client'}
                    initialAssignedToUserId={currentStall.assignedToUserId}
                    onOpen={() => updateEventMutation.reset()}
                    onConfirm={(note, category, assignedToUserId) =>
                      updateEventMutation
                        .mutateAsync({ eventId: currentStall.id, note, category, assignedToUserId })
                        .then(() => undefined)
                    }
                  />
                  <ConfirmActionItem
                    label={resumeAction.label}
                    itemDisabled={actionsDisabled}
                    onConfirm={resumeAction.onSelect}
                    confirm={resumeAction.confirm}
                  />
                </>
              ) : null}
              <DropdownMenu.Separator />
              <AlertDialog.Root>
                <AlertDialog.Trigger>
                  <DropdownMenu.Item color="red" onSelect={(e) => e.preventDefault()}>
                    Delete
                  </DropdownMenu.Item>
                </AlertDialog.Trigger>
                <AlertDialog.Content>
                  <AlertDialog.Title>Delete installation #{installation.installation_id}?</AlertDialog.Title>
                  <AlertDialog.Description size="2">This cannot be undone.</AlertDialog.Description>
                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray">
                        Cancel
                      </Button>
                    </AlertDialog.Cancel>
                    <AlertDialog.Action>
                      <Button color="red" onClick={handleDelete}>
                        Delete
                      </Button>
                    </AlertDialog.Action>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          {/* Green only while it is clickable: Radix paints a disabled solid button gray
              whatever its color, so the accent never shows for "nothing to save". */}
          <Button
            variant="solid"
            color="green"
            disabled={!isDirty || updateMutation.isPending}
            onClick={onSubmit}
            loading={updateMutation.isPending}
          >
            Save
          </Button>
        </Flex>
      </Flex>

      {updateMutation.error ? (
        <Text color="red" size="2">
          {updateMutation.error.message}
        </Text>
      ) : null}
      {deleteMutation.error ? (
        <Text color="red" size="2">
          {deleteMutation.error.message}
        </Text>
      ) : null}

      {currentStall && (
        <Callout.Root color="red" variant="surface">
          <Flex direction="column" gap="1">
            <Flex align="center" gap="2" wrap="wrap">
              <Text size="2" weight="medium">
                Stalled · {integerDaysSince(currentStall.occurredAt)}d ago
              </Text>
              {currentStall.category && (
                <Badge color="red" variant="soft" size="1">
                  {stalledBecauseOfLabel[currentStall.category]}
                </Badge>
              )}
              <AssigneeBadge assignee={currentStall} />
            </Flex>
            <ViewInstallationEventLink eventId={currentStall.id} />
          </Flex>
        </Callout.Root>
      )}

      {isRetired ? (
        <Callout.Root color="gray" variant="surface">
          <Callout.Icon>
            <ArchiveIcon />
          </Callout.Icon>
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Installation retired on {installation.retired_at}
            </Text>
            {installation.retirement_reason ? (
              <Text size="2" color="gray">
                {installation.retirement_reason}
              </Text>
            ) : null}
          </Flex>
        </Callout.Root>
      ) : (
        <ProgressStepper stages={stepperStages} />
      )}

      {/* One grid, no row spans: each row stretches its cards to a common height, so the
          media cards fill a height the form dictates instead of dictating one themselves. */}
      <Grid columns="3" gap="4">
        <Card className="col-span-2">
          <Heading size="3" mb="3">
            Site &amp; contacts
          </Heading>
          <DataList.Root>
            <LinkDataItem
              label="Organization"
              value={installation.org?.name ?? null}
              valueNode={
                installation.org_id && installation.org?.name ? (
                  <OrgLink orgId={installation.org_id} name={installation.org.name} size="2" />
                ) : undefined
              }
              subtle={installation.org_id}
            />
            <LinkDataItem
              label="Site"
              value={installation.site?.name ?? null}
              subtle={installation.site_id}
              subtleHref={installation.site_id ? webappSiteUrl(installation.site_id) : null}
            />
            <LinkDataItem
              label="Water station"
              value={installation.water_station?.name ?? null}
              subtle={installation.water_station_id}
              subtleHref={
                installation.site_id && installation.water_station_id
                  ? webappStationUrl(installation.site_id, installation.water_station_id)
                  : null
              }
            />
            {providerDeviceLink ? (
              <LinkDataItem
                label="Provider device"
                value={providerDeviceLink.label}
                subtle={providerDeviceLink.deviceId}
                subtleHref={providerDeviceLink.url}
              />
            ) : null}
          </DataList.Root>
          <Separator size="4" my="4" />
          <Grid columns="2" gap="4">
            <Controller
              name="siteContactId"
              control={control}
              render={({ field }) => (
                <ContactPicker orgId={installation.org_id} value={field.value ?? null} onChange={field.onChange} />
              )}
            />
            <Controller
              name="installationContactId"
              control={control}
              render={({ field }) => (
                <ContactPicker
                  orgId={installation.org_id}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  label="Installation contact"
                />
              )}
            />
          </Grid>
        </Card>

        <Card>
          <Flex direction="column" gap="3" className="h-full">
            <Heading size="3">Pre-installation photo</Heading>
            <Box className="min-h-0 flex-1">
              <PreInstallationImage
                storedRef={installation.pre_installation_image_url}
                installationId={installationId}
                canEdit
                onUploaded={(path) => {
                  updateMutation.mutate({
                    installationId,
                    preInstallationImageUrl: path,
                    preInstallationImageAddedAt: todayIso()
                  })
                }}
                onDeleted={() =>
                  updateMutation
                    .mutateAsync({
                      installationId,
                      preInstallationImageUrl: null,
                      preInstallationImageAddedAt: null
                    })
                    .then(() => undefined)
                }
              />
            </Box>
          </Flex>
        </Card>

        <Flex direction="column" gap="4" className="col-span-2">
          <CompatibilityCard installation={installation} />
          <Card>
            <Heading size="3" mb="3">
              Installation
            </Heading>
            <Grid columns="2" gap="3" align="start">
              <TextFieldEditor
                label="Logger serial number"
                name="loggerSerialNumber"
                control={control}
                placeholder="e.g. SHP-001234"
              />
              <TextFieldEditor
                label="Pulse weight"
                name="pulseWeight"
                control={control}
                type="number"
                placeholder="e.g. 1"
                parse={(raw) => (raw === '' ? null : Number(raw))}
                unit="L"
              />
              <TextFieldEditor
                label="Planned installation date"
                name="plannedInstallationDate"
                control={control}
                type="date"
              />
            </Grid>
          </Card>
        </Flex>

        {/* Kept alongside the pre-installation photo, not replacing it: the gallery is a
            storage listing and still holds every photo emailed in before the assistant existed. */}
        <InstallationPhotos installationId={installationId} />
      </Grid>

      <MeterPhotosCard installation={installation} />

      <InstallationCaptures installationId={installationId} waterStationId={installation.water_station_id} />

      <InstallationMaterialReturns installationId={installationId} />

      <InstallationTickets
        installationId={installationId}
        siteContactEmail={installation.site_contact?.email}
        siteContactName={installation.site_contact?.name}
      />

      <InstallationComments
        installationId={installationId}
        isAlreadyStalled={isStalled}
        events={installationEvents}
      />
    </Flex>
  )
}

export const Route = createFileRoute('/installations/$installationId')({
  component: InstallationDetailPage
})
